import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner, Row, Input } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios from "axios";
import useCurrency, { baseUrl } from "../../../Utils/FileUpload";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaFilePdf, FaFileExcel } from "react-icons/fa";
import { exportToPDF } from "../../../Utils/pdfExportUtils";
import { excelExportUtils } from "../../../Utils/excelExportUtils";

interface CashFlow {
  id: string;
  date: string;
  received_amount: string;
  total_expense: string;
  received_amount_without_currency: string;
  total_expense_without_currency: string;
}
interface CashFlowWithFooter extends CashFlow {
  isFooter?: boolean;
}
export default function CashFlowReport() {
  const [filteredItems, setFilteredItems] = useState<CashFlow[]>([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const { currency } = useCurrency();
  // console.log(currency);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchTriggered, setSearchTriggered] = useState<boolean>(false);

  const formatDate = (date: Date | null): string | null => {
    if (!date) return null;

    // Ensure local YYYY-MM-DD format
    const year = date.getFullYear();
    const month = `0${date.getMonth() + 1}`.slice(-2); // Month is 0-indexed
    const day = `0${date.getDate()}`.slice(-2);

    return `${year}-${month}-${day}`;
  };

  const fetchData = async (page = 1, perPage = 10) => {
    setLoading(true);
    try {
      const formattedFromDate = formatDate(fromDate);
      const formattedToDate = formatDate(toDate);

      const response = await axios.get(
        `${baseUrl}call_history/getDailyFinancialReport`,
        {
          params: {
            page,
            per_page: perPage,
            from_date: formattedFromDate,
            to_date: formattedToDate,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data: CashFlow[] = response.data.data;
      // console.log(data, "data");
      setFilteredItems(data);
      setTotalRows(response.data.total);
      setCurrentPage(response.data.current_page);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage, perPage);
  }, [currentPage, perPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    await fetchData(1, newPerPage);
  };

  const handleDateFilterClick = () => {
    if (fromDate && toDate && fromDate > toDate) {
      toast.error("From date cannot be greater than To date.");
      return;
    }
    setSearchTriggered(true);
    fetchData(currentPage, perPage);
  };
  const handleFullReset = () => {
    setFromDate(null);
    setToDate(null);
    setSearchTriggered(false);
    fetchData(currentPage, perPage); // direct fetch
  };
  useEffect(() => {
    // Only run for automatic resets, not manual searches

    if (!searchTriggered && fromDate === null && toDate === null) {
      fetchData(currentPage, perPage);
    }
  }, [searchTriggered, fromDate, toDate]);

  const getCashFlowColumns = () => {
    return [
      {
        name: "Date",
        cell: (row: CashFlowWithFooter) =>
          row.isFooter ? <strong>Total</strong> : row.date,
      },
      {
        name: "Amount Received",
        cell: (row: CashFlowWithFooter) =>
          row.isFooter ? (
            <strong>
              {currency} {totalAmount.toLocaleString()}
            </strong>
          ) : (
            row.received_amount
          ),
      },
      {
        name: "Total Expense",
        cell: (row: CashFlowWithFooter) =>
          row.isFooter ? (
            <strong>
              {currency} {totalAgentAmount.toLocaleString()}
            </strong>
          ) : (
            row.total_expense || "N/A"
          ),
      },
    ];
  };

  const calculateTotals = () => {
    const totalAmount = filteredItems.reduce((acc, row) => {
      const amount = parseFloat(
        (row.received_amount_without_currency || "0").replace(/,/g, "")
      );
      return acc + amount;
    }, 0);

    const totalAgentAmount = filteredItems.reduce((acc, row) => {
      const amount = parseFloat(
        (row.total_expense_without_currency || "0").replace(/,/g, "")
      );
      return acc + amount;
    }, 0);

    return {
      totalAmount,
      totalAgentAmount,
    };
  };
  const { totalAmount, totalAgentAmount } = calculateTotals();

  const columnsData: CashFlowWithFooter[] = [
    ...filteredItems,
    {
      isFooter: true,
      id: "",
      date: "Total",
      received_amount: "",
      total_expense: "",
      received_amount_without_currency: "",
      total_expense_without_currency: "",
    },
  ];
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner color="primary" />
      </div>
    );
  }

  const fetchAllDataWithoutPagination = async () => {
    try {
      const formattedFromDate = formatDate(fromDate);
      const formattedToDate = formatDate(toDate);
      const response = await axios.get(
        `${baseUrl}call_history/getDailyFinancialReportNoPagination`,
        {
          params: {
            from_date: formattedFromDate,
            to_date: formattedToDate,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // console.log(response.data, "data"); // Use response.data directly
      return response.data; // Return the response directly
    } catch (error) {
      console.error("Error fetching all data without pagination:", error);
      return []; // Return an empty array on error
    }
  };

  const customStyles = {
    headCells: {
      style: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#308e87",
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #308e87",
      },
    },
    rows: {
      style: {
        borderBottom: "1px solid #dee2e6",
      },
    },
    table: {
      style: {
        border: "1px solid #dee2e6",
        borderRadius: "8px",
      },
    },
  };

  const conditionalRowStyles = [
    {
      when: (row: any) => row.isFooter === true,
      style: {
        fontWeight: "800",
        backgroundColor: "#f8f9fa",
        color: "#308e87",
        borderTop: "2px solid #dee2e6",
        fontSize: "16px",
      },
    },
  ];

  const exportPDF = async () => {
    const allData = await fetchAllDataWithoutPagination();
    const tableData = allData.map((row: any) => [
      row.date || "N/A",
      row.received_amount_without_currency || "N/A",
      row.total_expense_without_currency || "N/A",
    ]);

    const footerRow = ["Total", `${totalAmount}`, `${totalAgentAmount}`];

    exportToPDF({
      fileName: "Cash_Flow_Report",
      columns: ["Date", "Agent Received", "Total Expense"],
      data: tableData,
      emptyDataMessage: "No cash flow available for export.",
      footerRow,
    });
  };
  const exportExcel = async () => {
    // 1️⃣ fetch & guard
    const allData = await fetchAllDataWithoutPagination();
    if (!allData.length) {
      toast.warn("No cash flow available for export.", {
        position: "top-right",
        autoClose: 1000,
      });
      return;
    }

    // 2️⃣ map rows & accumulate numeric totals
    const tableData = allData.map((row: CashFlow) => {
      return {
        Date: row.date || "N/A",
        "Amount Received": row.received_amount || "N/A",
        "Total Expense": row.total_expense || "N/A",
      };
    });
    // Add footer row
    const footerRow = {
      Date: "Total",
      "Amount Received": `${currency ? ` ${currency}` : ""}${totalAmount}`,
      "Total Expense": `${currency ? ` ${currency}` : ""}${totalAgentAmount}`,
    };

    // 4️⃣ export in one line
    excelExportUtils(
      tableData,
      "Cash_Flow_Report", // → Cash_Flow_Report.xlsx
      "Cash Flow", // worksheet/tab name
      footerRow // auto-appended total row
    );
  };

  return (
    <>
      <Breadcrumbs
        pageTitle="Cash Flow Report"
        parent="Reports"
        title="Cash Flow Report"
      />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody>
            <Row className="align-items-center justify-content-end">
              <Col xl={2}>
                <div className="d-flex gap-2">
                  <Button
                    color="primary"
                    onClick={exportPDF}
                    className="d-flex justify-content-center  p-0"
                    style={{ width: "30px", height: "30px" }}
                    title="PDF"
                  >
                    <FaFilePdf size={16} />
                  </Button>
                  <Button
                    color="secondary"
                    onClick={exportExcel}
                    className="d-flex justify-content-center  p-0"
                    style={{ width: "30px", height: "30px" }}
                    title="EXCEL"
                  >
                    <FaFileExcel size={16} />
                  </Button>
                </div>
              </Col>

              <Col xl={5}></Col>

              <Col xl={5}>
                <div className="d-flex align-items-center gap-2">
                  <label className="mb-0">From:</label>
                  <div style={{ flex: 1 }}>
                    <DatePicker
                      selected={fromDate}
                      onChange={(date: Date | null) => {
                        // Note the null type
                        setFromDate(date);
                        if (!date && !toDate) {
                          // Reset if both dates are empty
                          handleFullReset();
                        }
                      }}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      placeholderText="Start date"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      maxDate={new Date()}
                    />
                  </div>

                  <label className="mb-0">To:</label>
                  <div style={{ flex: 1 }}>
                    <DatePicker
                      selected={toDate}
                      onChange={(date: Date | null) => {
                        // Note the null type
                        setToDate(date);
                        if (!date && !fromDate) {
                          // Reset if both dates are empty
                          handleFullReset();
                        }
                      }}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      placeholderText="End date"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      maxDate={new Date()}
                    />
                  </div>
                  <Button
                    color="primary"
                    onClick={handleDateFilterClick}
                    title="Filter"
                  >
                    <i className="fa fa-filter" />
                  </Button>
                  <Button
                    color="danger"
                    onClick={handleFullReset} // Changed to use the common reset
                    title="Reset Filter"
                  >
                    <i className="fa fa-refresh" />
                  </Button>
                </div>
              </Col>
            </Row>

            <div className="table-responsive custom-scrollbar mt-3">
              <DataTable
                data={columnsData}
                columns={getCashFlowColumns()}
                highlightOnHover
                striped
                customStyles={customStyles}
                pagination
                paginationServer
                paginationPerPage={perPage}
                paginationTotalRows={totalRows}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                paginationDefaultPage={currentPage}
                responsive
                conditionalRowStyles={conditionalRowStyles}
              />
            </div>
          </CardBody>
        </Card>
      </Col>

      <ToastContainer />
    </>
  );
}
